import {
  BiBlendJoin,
  BiBlendKey,
  BiCardQuery,
  BiCardQueryKind,
  biBlendKeyColumn,
  biBlendMeasureKey,
  isBiBlendSpec,
  isBiTotalsSpec,
  kBiMaxTotalsParts,
} from '@euler/model/bi/blend';
import { BiCardView, BiDashboardFilters } from '@euler/model/bi/dashboard';
import { BiFormatSpec } from '@euler/model/bi/format';
import {
  BiDatasetRef,
  BiDimensionRef,
  BiFilter,
  BiMeasureFilter,
  BiMeasureRef,
  BiQuerySpec,
  BiSort,
  BiTimeRange,
  biDimensionKey,
  biMeasureKey,
} from '@euler/model/bi/query';
import { BiQueryResult } from '@euler/model/bi/result';
import {
  BiChartType,
  BiChartViz,
  BiColor,
  BiFunnelStagesBy,
  BiKpiCompare,
  BiKpiGroupItem,
  BiVizSpec,
  BiVizStyle,
  BiVizType,
  biVizStyleFitting,
  isBiKpiViz,
  kBiMaxFunnelStages,
  kBiMaxKpiGroupItems,
  kBiMaxRadarSeries,
  kBiMinFunnelStages,
  kBiMinKpiGroupItems,
  kBiPyramidSides,
} from '@euler/model/bi/viz';
import { biCaseProblem } from './biEditorCase';
import { BiSeriesLook, biComboSeries, biSeriesLooksOf } from './biEditorCombo';

/** Which card the editor works on. */
export type BiEditorTarget =
  | { kind: 'new' }
  /** `revision`: the dashboard's when the form opened, the save's base. */
  | {
      kind: 'edit';
      card: BiCardView;
      revision: number;
      /**
       * The result the card showed when the form opened and the filters it
       * was viewed under: the preview starts from it instead of a query.
       */
      loaded?: BiEditorLoaded;
    };

export interface BiEditorLoaded {
  result: BiQueryResult;
  filters: BiDashboardFilters;
}

/** One query's parts as the form edits them; a dataset may be unpicked. */
export interface BiEditorQuery {
  dataset: BiDatasetRef | null;
  measures: BiMeasureRef[];
  dimensions: BiDimensionRef[];
  filters: BiFilter[];
  timeRange?: BiTimeRange;
  sort: BiSort[];
  limit?: number;
  /** Drops the groups whose every measure is zero or empty. */
  excludeEmpty?: boolean;
  /**
   * Conditions on measures per group, kept from a card the assistant made;
   * the form does not edit them.
   */
  having?: BiMeasureFilter[];
}

/**
 * The second query of a blend and how the two merge. Each side's
 * dimensions are exactly its fields carrying the `on` keys, kept so by the
 * blend operations (`biEditorBlend.ts`).
 */
export interface BiEditorBlend {
  second: BiEditorQuery;
  on: BiBlendKey[];
  join: BiBlendJoin;
}

/** How a KPI group shows one of its numbers, apart from its value. */
export interface BiKpiItemLook {
  name?: string;
  color?: BiColor;
}

/** What the card editor holds: the first query, a blend, the chart type. */
export interface BiEditorState extends BiEditorQuery {
  title: string;
  /** Combined with a second dataset; null for one dataset. */
  blend: BiEditorBlend | null;
  /**
   * A KPI group's other datasets: each a query of its own whose totals sit
   * beside the first query's. Empty for one dataset.
   */
  totalsParts: BiEditorQuery[];
  vizType: BiVizType;
  /** A KPI or KPI group compares with the previous period (needs ranges). */
  kpiCompare: boolean;
  /**
   * The look as set so far; options that do not fit the chart type stay
   * here while hidden, and are dropped from the saved viz.
   */
  style?: BiVizStyle;
  /** Combo charts: a series' own type and axis, by measure key. */
  seriesLooks?: Record<string, BiSeriesLook>;
  /** 数字格式: the card's own format per measure key, over the column's. */
  formats?: Record<string, BiFormatSpec>;
  /** A KPI group's own item names and colours, by measure key. */
  kpiItems?: Record<string, BiKpiItemLook>;
  /**
   * A funnel over a dimension's values: the card's stage order, kept while
   * the dimension stays; the form draws funnels of measures otherwise.
   */
  stagesBy?: BiFunnelStagesBy;
}

export const kBiEditorMaxLimit = 1000;

function queryOfSpec(spec: BiQuerySpec): BiEditorQuery {
  return {
    dataset: spec.dataset,
    measures: spec.measures,
    dimensions: spec.dimensions,
    filters: spec.filters,
    ...(spec.timeRange ? { timeRange: spec.timeRange } : {}),
    sort: spec.sort,
    ...(spec.limit !== undefined ? { limit: spec.limit } : {}),
    ...(spec.excludeEmpty ? { excludeEmpty: true } : {}),
    ...(spec.having?.length ? { having: spec.having } : {}),
  };
}

function formatsOf(viz: BiVizSpec): Record<string, BiFormatSpec> | undefined {
  if (viz.type === BiVizType.KpiGroup) {
    const entries = viz.items.flatMap(item =>
      item.format ? [[item.value, item.format] as const] : [],
    );
    return entries.length ? Object.fromEntries(entries) : undefined;
  }
  if (viz.type !== BiVizType.Kpi) return viz.formats;
  return viz.format ? { [viz.value]: viz.format } : undefined;
}

export function biEditorStateFromCard(card: BiCardView): BiEditorState {
  const { spec, viz } = card;
  const blend = isBiBlendSpec(spec);
  const totals = isBiTotalsSpec(spec);
  const looks = biSeriesLooksOf(viz);
  const formats = formatsOf(viz);
  const kpiItems =
    viz.type === BiVizType.KpiGroup
      ? Object.fromEntries(
          viz.items
            .filter(item => item.name || item.color)
            .map(item => [
              item.value,
              {
                ...(item.name ? { name: item.name } : {}),
                ...(item.color ? { color: item.color } : {}),
              },
            ]),
        )
      : {};
  return {
    title: card.title,
    ...queryOfSpec(blend || totals ? spec.queries[0] : spec),
    blend: blend
      ? {
          second: queryOfSpec(spec.queries[1]),
          on: spec.on,
          join: spec.join,
        }
      : null,
    totalsParts: totals ? spec.queries.slice(1).map(queryOfSpec) : [],
    vizType: viz.type,
    kpiCompare: isBiKpiViz(viz) && viz.compare === BiKpiCompare.PreviousPeriod,
    ...(viz.style ? { style: viz.style } : {}),
    ...(looks ? { seriesLooks: looks } : {}),
    ...(formats ? { formats } : {}),
    ...(Object.keys(kpiItems).length ? { kpiItems } : {}),
    ...(viz.type === BiVizType.Funnel && viz.stagesBy
      ? { stagesBy: viz.stagesBy }
      : {}),
  };
}

export function biEditorQueryNew(): BiEditorQuery {
  return {
    dataset: null,
    measures: [],
    dimensions: [],
    filters: [],
    sort: [],
  };
}

export function biEditorStateNew(): BiEditorState {
  return {
    title: '',
    ...biEditorQueryNew(),
    blend: null,
    totalsParts: [],
    vizType: BiVizType.Bar,
    kpiCompare: false,
  };
}

/**
 * A new dataset clears every part that named the old one's fields; in a
 * blend also the merge keys, which both datasets' fields carried.
 */
export function biEditorWithDataset(
  state: BiEditorState,
  dataset: BiDatasetRef,
): BiEditorState {
  return {
    ...state,
    dataset,
    measures: [],
    dimensions: [],
    filters: [],
    sort: [],
    blend: state.blend
      ? {
          ...state.blend,
          on: [],
          second: { ...state.blend.second, dimensions: [] },
        }
      : null,
  };
}

/** The result keys one query returns: its dimensions, then its measures. */
export function biEditorQueryKeys(query: BiEditorQuery): string[] {
  return [
    ...query.dimensions.map(biDimensionKey),
    ...query.measures.map(biMeasureKey),
  ];
}

/**
 * The card's result keys: a blend's merge-key columns and each side's
 * prefixed measures (`q1__…`, `q2__…`), or the one query's own.
 */
export function biEditorCardKeys(state: BiEditorState): {
  dimensions: string[];
  measures: string[];
} {
  if (state.totalsParts.length)
    return {
      dimensions: [],
      measures: [state, ...state.totalsParts].flatMap((part, at) =>
        part.measures.map(ref => biBlendMeasureKey(at, ref)),
      ),
    };
  if (!state.blend)
    return {
      dimensions: state.dimensions.map(biDimensionKey),
      measures: state.measures.map(biMeasureKey),
    };
  return {
    dimensions: state.blend.on.map(biBlendKeyColumn),
    measures: [
      ...state.measures.map(ref => biBlendMeasureKey(0, ref)),
      ...state.blend.second.measures.map(ref => biBlendMeasureKey(1, ref)),
    ],
  };
}

/** Whether the chart's first dimension (its x) is a time at a grain. */
function xIsTime(state: BiEditorState): boolean {
  const first = state.blend ? state.blend.on[0] : state.dimensions[0];
  return !!first?.grain;
}

/**
 * Why a radar or a pyramid cannot draw the card's shape, or null: a radar
 * charts 1 to 5 measures (or one split by a second dimension), a pyramid a
 * category and exactly two measures (or one split into two sides).
 */
export function biEditorSidedProblem(state: BiEditorState): string | null {
  const { dimensions, measures } = biEditorCardKeys(state);
  const split = dimensions.length === 2;
  if (state.vizType === BiVizType.Radar) {
    if (!split && measures.length > kBiMaxRadarSeries)
      return `雷达图最多画 ${kBiMaxRadarSeries} 个指标`;
    return null;
  }
  if (state.vizType === BiVizType.Pyramid) {
    if (xIsTime(state)) return '金字塔图按类别左右对比，第一个维度不能是时间';
    if (!split && measures.length !== kBiPyramidSides)
      return '金字塔图需要正好两个指标，左右各一边';
  }
  return null;
}

/**
 * Why a funnel cannot draw the card's shape, or null: its stages are the
 * measures in order (no dimension), or the values of the card's own stage
 * dimension.
 */
function funnelProblem(state: BiEditorState): string | null {
  const dims = state.dimensions.map(biDimensionKey);
  if (dims.length === 0)
    return state.measures.length < kBiMinFunnelStages
      ? `漏斗图至少需要 ${kBiMinFunnelStages} 个指标，按步骤顺序排列`
      : state.measures.length > kBiMaxFunnelStages
      ? `漏斗图最多 ${kBiMaxFunnelStages} 步`
      : null;
  if (dims.length === 1 && state.stagesBy?.key === dims[0])
    return state.measures.length === 1
      ? null
      : '按维度分步的漏斗图只能选一个指标';
  return '漏斗图按指标顺序画各步，请去掉维度';
}

function blendProblem(state: BiEditorState, blend: BiEditorBlend) {
  if (!blend.second.dataset) return '选择要组合的数据集';
  if (!blend.second.measures.length) return '组合的数据至少选择一个指标';
  if (!blend.on.length) return '至少选择一个关联字段';
  switch (state.vizType) {
    case BiVizType.Kpi:
      return '组合数据不能用指标卡，请换成图表或表格';
    case BiVizType.KpiGroup:
      return '组合数据不能用指标组，请换成图表或表格';
    case BiVizType.Table:
      return null;
    case BiVizType.Pie:
    case BiVizType.Donut:
      return '饼图只能展示一个指标，组合数据请换成其他图表';
    case BiVizType.Funnel:
      return '组合数据不能用漏斗图，请换成其他图表';
    default:
      if (blend.on.length > 1)
        return '组合数据的图表只能按一个关联字段作横轴；多个关联字段请用表格';
      return biEditorSidedProblem(state);
  }
}

/**
 * Why a KPI group cannot show the card: its numbers are one row (no
 * dimension), 2 to 6 of them across every dataset, each part complete, and
 * a comparison needs every part's time range.
 */
function kpiGroupProblem(state: BiEditorState): string | null {
  const parts = [state, ...state.totalsParts];
  if (state.dimensions.length) return '指标组不按维度分组，请去掉维度';
  if (parts.some(part => !part.dataset)) return '选择要合计的数据集';
  if (parts.some(part => !part.measures.length))
    return '每份数据至少选择一个指标';
  const count = parts.reduce((sum, part) => sum + part.measures.length, 0);
  if (count < kBiMinKpiGroupItems || count > kBiMaxKpiGroupItems)
    return `指标组展示 ${kBiMinKpiGroupItems} ～ ${kBiMaxKpiGroupItems} 个指标`;
  if (state.kpiCompare && parts.some(part => !part.timeRange))
    return '对比上期需要每份数据都选时间范围';
  return null;
}

/** Why the state cannot be previewed or saved yet, or null. */
export function biEditorProblem(state: BiEditorState): string | null {
  const dims = state.dimensions.length;
  const measures = state.measures.length;
  if (!state.dataset) return '先选择数据集';
  if (!measures) return '至少选择一个指标';
  if (state.blend) return blendProblem(state, state.blend);
  if (state.totalsParts.length && state.vizType !== BiVizType.KpiGroup)
    return '几份数据的合计只能用指标组展示';
  if (state.totalsParts.length + 1 > kBiMaxTotalsParts)
    return `指标组最多合计 ${kBiMaxTotalsParts} 份数据`;
  for (const ref of state.dimensions) {
    const problem = ref.case ? biCaseProblem(ref.case) : null;
    if (problem) return problem;
  }
  switch (state.vizType) {
    case BiVizType.KpiGroup:
      return kpiGroupProblem(state);
    case BiVizType.Kpi:
      if (dims) return '指标卡不按维度分组，请去掉维度';
      if (state.kpiCompare && !state.timeRange)
        return '对比上期需要先选时间范围';
      return null;
    case BiVizType.Table:
      return null;
    case BiVizType.Pie:
    case BiVizType.Donut:
      if (dims !== 1 || measures !== 1) return '饼图需要一个维度和一个指标';
      return null;
    case BiVizType.Funnel:
      return funnelProblem(state);
    default:
      if (!dims) return '图表需要至少一个维度作为横轴';
      if (dims > 2) return '图表最多两个维度';
      if (dims === 2 && measures !== 1)
        return '两个维度时只能选一个指标（第二个维度作为系列）';
      return biEditorSidedProblem(state);
  }
}

function querySpecOf(query: BiEditorQuery): BiQuerySpec | null {
  if (!query.dataset) return null;
  const keys = new Set(biEditorQueryKeys(query));
  return {
    dataset: query.dataset,
    measures: query.measures,
    dimensions: query.dimensions,
    filters: query.filters,
    ...(query.timeRange ? { timeRange: query.timeRange } : {}),
    // A sort on a key the query no longer returns would be refused.
    sort: query.sort.filter(entry => keys.has(entry.key)),
    ...(query.limit !== undefined ? { limit: query.limit } : {}),
    // Dropping empty groups needs groups: it goes with the last dimension.
    ...(query.excludeEmpty && query.dimensions.length
      ? { excludeEmpty: true }
      : {}),
    ...havingOf(query, keys),
  };
}

/**
 * The conditions still on measures the query returns; they need groups,
 * like dropping empty ones.
 */
function havingOf(
  query: BiEditorQuery,
  keys: Set<string>,
): Pick<BiQuerySpec, 'having'> {
  const having = query.dimensions.length
    ? (query.having ?? []).filter(entry => keys.has(entry.measure))
    : [];
  return having.length ? { having } : {};
}

export function biEditorSpec(state: BiEditorState): BiCardQuery | null {
  const first = querySpecOf(state);
  if (first && state.totalsParts.length) {
    const parts = state.totalsParts.map(querySpecOf);
    if (parts.some(part => !part)) return null;
    // A total is one row: there is nothing for a sort to order.
    return {
      kind: BiCardQueryKind.Totals,
      queries: [first, ...(parts as BiQuerySpec[])].map(part => ({
        ...part,
        sort: [],
      })),
    };
  }
  if (!first || !state.blend) return first;
  const second = querySpecOf(state.blend.second);
  if (!second) return null;
  // A blend merges whole groups; it keeps no condition on them.
  return {
    kind: BiCardQueryKind.Blend,
    queries: [first, second].map(({ having: _having, ...side }) => side),
    on: state.blend.on,
    join: state.blend.join,
  };
}

function names(
  original: BiVizSpec | undefined,
): Map<string, string | undefined> {
  if (!original || isBiKpiViz(original)) return new Map();
  const entries =
    original.type === BiVizType.Table ? original.columns : original.series;
  return new Map(entries.map(entry => [entry.key, entry.name]));
}

/**
 * A KPI group's items over the measures it shows: each keeps the name and
 * colour the card gave it, and takes the state's format.
 */
function kpiGroupItems(
  measureKeys: string[],
  formats: Record<string, BiFormatSpec> | undefined,
  looks: Record<string, BiKpiItemLook> | undefined,
): BiKpiGroupItem[] {
  return measureKeys.map(value => {
    const { name, color } = looks?.[value] ?? {};
    const format = formats?.[value];
    return {
      value,
      ...(name ? { name } : {}),
      ...(color ? { color } : {}),
      ...(format ? { format } : {}),
    };
  });
}

/**
 * The viz for the state's chart type over its result keys. Display extras
 * of the card's own viz (names) carry over where keys still match, its
 * number formats stay on the measures it still shows,
 * and the look keeps what fits the type (`biVizStyleFitting`).
 */
export function biEditorViz(
  state: BiEditorState,
  original?: BiVizSpec,
): BiVizSpec | null {
  const viz = unstyledViz(state, original);
  if (!viz) return null;
  const style = biVizStyleFitting(state.style, viz, original);
  return style ? { ...viz, style } : viz;
}

function unstyledViz(
  state: BiEditorState,
  original?: BiVizSpec,
): BiVizSpec | null {
  if (biEditorProblem(state)) return null;
  const kept = names(original);
  const named = (key: string) => {
    const name = kept.get(key);
    return name ? { key, name } : { key };
  };
  const { dimensions: dimensionKeys, measures: measureKeys } =
    biEditorCardKeys(state);
  // Only a measure the card still shows keeps its format.
  const shown = Object.entries(state.formats ?? {}).filter(([key]) =>
    measureKeys.includes(key),
  );
  const formats = shown.length ? Object.fromEntries(shown) : undefined;
  if (state.vizType === BiVizType.Kpi) {
    const format = state.formats?.[measureKeys[0]];
    return {
      type: BiVizType.Kpi,
      value: measureKeys[0],
      ...(state.kpiCompare ? { compare: BiKpiCompare.PreviousPeriod } : {}),
      ...(format ? { format } : {}),
    };
  }
  if (state.vizType === BiVizType.KpiGroup)
    return {
      type: BiVizType.KpiGroup,
      items: kpiGroupItems(measureKeys, formats, state.kpiItems),
      ...(state.kpiCompare ? { compare: BiKpiCompare.PreviousPeriod } : {}),
    };
  if (state.vizType === BiVizType.Table)
    return {
      type: BiVizType.Table,
      columns: [...dimensionKeys, ...measureKeys].map(named),
      ...(formats ? { formats } : {}),
    };
  if (state.vizType === BiVizType.Funnel)
    return {
      type: BiVizType.Funnel,
      series: measureKeys.map(named),
      ...(dimensionKeys.length && state.stagesBy
        ? { stagesBy: state.stagesBy }
        : {}),
      ...(formats ? { formats } : {}),
    };
  const chart: BiChartViz = {
    type: state.vizType as BiChartType,
    x: dimensionKeys[0],
    series: (dimensionKeys.length === 2
      ? measureKeys.slice(0, 1)
      : measureKeys
    ).map(named),
  };
  if (dimensionKeys.length === 2) chart.seriesBy = dimensionKeys[1];
  if (formats) chart.formats = formats;
  chart.series = biComboSeries(chart, state.seriesLooks);
  return chart;
}

/**
 * Keys by measure, moved as the card's parts change: `move` gives a key's
 * new name, or null when its measure left the card.
 */
function rekeyed<T>(
  map: Record<string, T> | undefined,
  move: (key: string) => string | null,
): Record<string, T> | undefined {
  if (!map) return undefined;
  const entries = Object.entries(map).flatMap(([key, value]) => {
    const next = move(key);
    return next === null ? [] : [[next, value] as const];
  });
  return entries.length ? Object.fromEntries(entries) : undefined;
}

const kPartKey = /^q(\d+)__(.+)$/;

/**
 * The state with other totals parts: a part added after the first, or one
 * removed (`removed`, 0 for the second dataset). Formats and item looks
 * follow their measures, whose keys carry the part (`q2__…`), and lose the
 * prefix again when one dataset is left.
 */
export function biEditorWithTotalsParts(
  state: BiEditorState,
  parts: BiEditorQuery[],
  removed?: number,
): BiEditorState {
  const was = state.totalsParts.length > 0;
  const is = parts.length > 0;
  const move = (key: string): string | null => {
    const match = kPartKey.exec(key);
    if (!was) return is ? `q1__${key}` : key;
    if (!match) return key;
    let part = Number(match[1]);
    if (removed !== undefined) {
      const gone = removed + 2;
      if (part === gone) return null;
      if (part > gone) part -= 1;
    }
    if (!is) return part === 1 ? match[2] : null;
    return `q${part}__${match[2]}`;
  };
  const formats = rekeyed(state.formats, move);
  const kpiItems = rekeyed(state.kpiItems, move);
  return {
    ...state,
    totalsParts: parts,
    formats,
    kpiItems,
  };
}
